import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import {
  searchAssociations,
  toAbsoluteLogoUrl,
} from "../../../services/associationsServices";

import {
  ensureArray,
  normalizeAssociation,
  normalizeSearchText,
} from "../utils/associationsUtils";

const INITIAL_FILTERS = {
  q: "",
  categorie: "",
};

function extractSearchItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  return ensureArray(response?.items);
}

export function useAssociationsPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [expandedMapId, setExpandedMapId] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadAssociations = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await searchAssociations({
        is_active: true,
        limit: 500,
        page: 1,
      });

      const rows = extractSearchItems(response);

      const normalizedRows = rows.map((association) =>
        normalizeAssociation(association, {
          resolveLogoUrl: toAbsoluteLogoUrl,
        })
      );

      setAllItems(normalizedRows);
    } catch (err) {
      console.error("Erreur de chargement associations:", err);
      setAllItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadAssociations();
  }, [loadAssociations]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        allItems
          .map((association) => association.category)
          .filter(Boolean)
          .map((category) => String(category).trim())
      )
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);
    const selectedCategory = String(filters.categorie || "").trim();

    return allItems.filter((association) => {
      const matchCategory = selectedCategory
        ? association.category === selectedCategory
        : true;

      const matchSearch = q ? association.searchIndex.includes(q) : true;

      return matchCategory && matchSearch;
    });
  }, [allItems, deferredSearch, filters.categorie]);

  const updateFilter = useCallback((name, value) => {
    setExpandedMapId(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setExpandedMapId(null);
    setFilters(INITIAL_FILTERS);
  }, []);

  const toggleCategory = useCallback((category) => {
    setExpandedMapId(null);

    setFilters((previous) => ({
      ...previous,
      categorie: previous.categorie === category ? "" : category,
    }));
  }, []);

  const toggleMap = useCallback((associationId) => {
    setExpandedMapId((currentId) =>
      currentId === associationId ? null : associationId
    );
  }, []);

  return {
    loading,
    error,
    items: filteredItems,
    total: filteredItems.length,
    allTotal: allItems.length,
    categories,
    filters,
    expandedMapId,
    updateFilter,
    resetFilters,
    toggleCategory,
    toggleMap,
    refresh: loadAssociations,
  };
}